import { useQuery } from '@tanstack/react-query';
import type { TemplateVersion } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Badge } from '../../../components/ui/Badge';
import { DefinitionList } from '../../../components/ui/DefinitionList';
import { Spinner } from '../../../components/ui/Spinner';
import { formatCount, formatKm, formatMoney } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

export interface TemplatePreviewTabProps {
  version: TemplateVersion;
}

const OUTPUT_KEYS = ['appraisal', 'ceiling', 'retail', 'insurance', 'registration', 'transfer', 'holding'] as const;

/**
 * What these values do to a price (plan §7).
 *
 * The vehicle is the **built-in reference sample** — the same car for every template — so two versions
 * can be compared value for value: the only thing that moved is the numbers this version carries
 * (PP AC20, AC21). The equations come from the tenant's published policy, which the note names.
 */
export const TemplatePreviewTab = ({ version }: TemplatePreviewTabProps) => {
  const { t } = useI18nTranslation();

  const previewQuery = useQuery(orpc.templates.previewVersion.queryOptions({ input: { versionId: version.id } }));

  if (previewQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (previewQuery.isError || !previewQuery.data) {
    return <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{t('errors.loadFailed')}</p>;
  }

  const preview = previewQuery.data;

  return (
    <div className="space-y-4">
      <section className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('templates.previewTitle')}</h2>
          <Badge mono>{t('templates.versionBadge', { version: preview.version })}</Badge>
          <Badge>{t('templates.previewPolicyBadge', { version: preview.policyVersion })}</Badge>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('templates.previewSample', {
            modelYear: preview.sample.modelYear,
            valuationYear: preview.sample.valuationYear,
            referencePrice: formatMoney(preview.sample.referencePrice),
            odometer: formatKm(preview.sample.odometerKm),
            owners: formatCount(preview.sample.ownerCount),
          })}
        </p>

        <DefinitionList
          items={OUTPUT_KEYS.map((key) => ({
            label: t(`templates.outputs.${key}`),
            value:
              preview.outputs[key] === null ? (
                <span className="text-muted-foreground">{t('templates.notCalculated')}</span>
              ) : (
                <span
                  dir="ltr"
                  className="inline-block font-mono text-sm tabular-nums"
                >
                  {formatMoney(preview.outputs[key], { exact: true })}
                </span>
              ),
          }))}
        />

        <p className="text-xs text-muted-foreground">{t('templates.previewNote')}</p>
      </section>

      {preview.issues.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          <p className="font-medium">{t('templates.previewIssues', { count: preview.issues.length })}</p>
          <ul className="mt-2 space-y-1">
            {preview.issues.slice(0, 8).map((issue, index) => (
              <li key={`${issue}-${index}`}>{issue}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
